/**
 * SEO Context — single source of truth for all <head> meta tags.
 *
 * Architecture:
 *  - ONE <Helmet> lives in PublicLayout, reads from this context.
 *  - Each page calls useSEO() or renders <SEO> to push its data in.
 *  - On every route change, PublicLayout clears the context first,
 *    so stale tags from the previous page NEVER linger in <head>.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

export interface SEOData {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object | object[];
}

interface SEOContextValue {
  data: SEOData | null;
  setSEO: (data: SEOData) => void;
}

export const SEOContext = createContext<SEOContextValue>({
  data: null,
  setSEO: () => {},
});

export function SEOProvider({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<SEOData | null>(null);

  const setSEO = useCallback((incoming: SEOData) => {
    setData(incoming);
  }, []);

  return (
    <SEOContext.Provider value={{ data, setSEO }}>
      {children}
    </SEOContext.Provider>
  );
}

export function useSEOContext() {
  return useContext(SEOContext);
}
